import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import "@/App.css";
import axios from "axios";
import {
  Stack,
  MagnifyingGlass,
  ArrowClockwise,
  CaretDown,
  CaretRight,
  Shield,
  Factory,
  Package,
  Timer,
  CalendarBlank,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Oct 17 2026, user's choice: NO automatic refresh. SAP is pulled at fixed IST
// times by the platform scheduler (.emergent/crons.yml ->
// /api/cron/wip-stage-refresh): 9:15 am, 1:30 pm, then every 4 hours from
// 5:30 pm (5:30 pm, 9:30 pm, 1:30 am, 5:30 am) - and whenever the user clicks
// "Refresh now". Nothing on this page re-reads on a timer.
const REFRESH_TIMES_IST = [
  { label: "1:30 am", hour: 1, minute: 30 },
  { label: "5:30 am", hour: 5, minute: 30 },
  { label: "9:15 am", hour: 9, minute: 15 },
  { label: "1:30 pm", hour: 13, minute: 30 },
  { label: "5:30 pm", hour: 17, minute: 30 },
  { label: "9:30 pm", hour: 21, minute: 30 },
];

// Minutes since IST midnight, read off the browser's own clock - the plant is
// on IST, but a laptop set to another zone must still see the right "next run".
const nowMinutesIST = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date());
  const hour = Number(parts.find((p) => p.type === "hour").value);
  const minute = Number(parts.find((p) => p.type === "minute").value);
  return hour * 60 + minute;
};

const nextRefreshLabel = () => {
  const now = nowMinutesIST();
  const next = REFRESH_TIMES_IST.find((t) => t.hour * 60 + t.minute > now);
  return next ? next.label : `${REFRESH_TIMES_IST[0].label} tomorrow`;
};

const formatQty = (value) => (value == null ? "—" : value.toLocaleString("en-IN", { maximumFractionDigits: 2 }));

const formatIST = (iso) => {
  if (!iso) return null;
  return (
    new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    }) + " IST"
  );
};

const formatDay = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "2-digit" });
};

// SAP sends "2025-01-29T11:13:44Z"; the date pickers speak "YYYY-MM-DD".
// The range matches each lot's ORDER RELEASE date (SAP's ReleaseDateTime) -
// the Production Lot query itself carries a date on only ~25% of open lots,
// so filtering on that would hide three quarters of the report.
const lotDate = (lot) => lot.release_date || lot.production_start_date || null;
const dayKey = (iso) => (iso ? iso.slice(0, 10) : null);

const StatCard = ({ icon: Icon, label, value, suffix, testId }) => (
  <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] flex flex-col gap-1.5" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[#475467]">
      <Icon size={14} weight="bold" />
      <span className="font-heading text-xs font-bold uppercase tracking-wider">{label}</span>
    </div>
    <span className="font-sans text-2xl font-bold tabular-nums text-[#1D2939]">
      {value}
      {suffix && <span className="text-sm font-medium text-[#667085] ml-1">{suffix}</span>}
    </span>
  </div>
);

// Sep 25 2026, user's explicit ask: "In-Process Qty by Stage" - how much is
// sitting at each Reporting Point right now, across every open production lot.
// Oct 17 2026, reworked to the user's own worked example (lot 75555): every
// stage shows its CONFIRMED qty next to the qty still WAITING for it, so a
// finished stage reads "100 confirmed / 0 in-process" instead of vanishing.
// Derived entirely from confirmation checkpoints (wip_stage_service.
// compute_wip_by_stage) - NOT a SAP stock location, so deliberately labelled
// "In-Process Qty", never "Stock".
export default function WipByStagePage() {
  const [status, setStatus] = useState("loading"); // loading | done | failed
  const [refreshing, setRefreshing] = useState(false);
  const [items, setItems] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const updatedAtRef = useRef(null);

  const loadFromCache = async ({ silent } = {}) => {
    if (!silent) {
      setStatus("loading");
      setError(null);
    }
    try {
      const { data } = await axios.get(`${API}/inventory/wip-by-stage`);
      setItems(data.items);
      setUpdatedAt(data.updated_at);
      updatedAtRef.current = data.updated_at;
      setStatus("done");
    } catch (err) {
      if (silent) return;
      setStatus("failed");
      setError(err?.response?.data?.detail || err.message);
    }
  };

  useEffect(() => {
    loadFromCache();
  }, []);

  // Query-filtered refresh: when a site is already selected, only THAT
  // site is re-pulled from SAP (fast) - every other site's cached
  // buckets are left as-is (see wip_stage_service.refresh_wip_stage_cache).
  //
  // Oct 3 2026: the all-sites pull now walks every page of SAP's open lots
  // (~2,250 lots, ~90s) instead of silently stopping at the first 500 - too
  // long for one request behind the 100s proxy limit - so the backend starts
  // it in the background and we poll the cache until the timestamp moves.
  const refresh = async () => {
    setRefreshing(true);
    const startedFrom = updatedAtRef.current;
    try {
      const params = siteFilter !== "all" ? { site_id: siteFilter } : {};
      await axios.post(`${API}/inventory/wip-by-stage`, null, { params });
      toast.info("Pulling every open lot from SAP - this takes about a minute");
      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await axios.get(`${API}/inventory/wip-by-stage`);
        if (data.updated_at && data.updated_at !== startedFrom) {
          setItems(data.items);
          setUpdatedAt(data.updated_at);
          updatedAtRef.current = data.updated_at;
          setStatus("done");
          toast.success("WIP by Stage refreshed from SAP");
          return;
        }
        if (!data.refreshing) break;
      }
      toast.error("Refresh did not finish", { description: "SAP is taking longer than usual - the background refresh will still complete on its own." });
    } catch (err) {
      toast.error("Refresh failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setRefreshing(false);
    }
  };

  const sites = useMemo(() => Array.from(new Set(items.map((it) => it.site_id).filter(Boolean))).sort(), [items]);

  const dateActive = Boolean(fromDate || toDate);

  // With a date range on, every row is re-totalled from only the lots that
  // started inside it (user's point 4: "then sum auto inprocess qty") - the
  // per-lot figures travel with each bucket, so no second SAP pull is needed.
  const { rows, undatedHidden } = useMemo(() => {
    const q = search.trim().toLowerCase();
    let undated = 0;
    const out = [];
    for (const it of items) {
      if (siteFilter !== "all" && it.site_id !== siteFilter) continue;
      if (q && !it.product_id.toLowerCase().includes(q)) continue;
      let lots = it.lots || [];
      let confirmed = it.confirmed_qty ?? 0;
      let inProcess = it.in_process_qty ?? 0;
      if (dateActive) {
        lots = lots.filter((l) => {
          const day = dayKey(lotDate(l));
          if (!day) {
            undated += 1;
            return false;
          }
          if (fromDate && day < fromDate) return false;
          if (toDate && day > toDate) return false;
          return true;
        });
        confirmed = lots.reduce((sum, l) => sum + (l.confirmed_qty || 0), 0);
        inProcess = lots.reduce((sum, l) => sum + (l.qty || 0), 0);
        if (confirmed <= 0 && inProcess <= 0) continue;
      }
      out.push({ ...it, lots, confirmed_qty: round4(confirmed), in_process_qty: round4(inProcess) });
    }
    out.sort(
      (a, b) =>
        (a.product_id || "").localeCompare(b.product_id || "") ||
        (a.site_id || "").localeCompare(b.site_id || "") ||
        (a.stage_order ?? 9999) - (b.stage_order ?? 9999) ||
        (a.reporting_point_id || "").localeCompare(b.reporting_point_id || ""),
    );
    return { rows: out, undatedHidden: undated };
  }, [items, search, siteFilter, fromDate, toDate, dateActive]);

  const toggleRow = (key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const distinctProducts = new Set(rows.map((it) => it.product_id)).size;
  const totalLots = new Set(rows.flatMap((it) => it.lots.map((l) => l.production_lot_id))).size;
  const totalInProcess = round4(rows.reduce((sum, it) => sum + (it.in_process_qty || 0), 0));
  const totalConfirmed = round4(rows.reduce((sum, it) => sum + (it.confirmed_qty || 0), 0));
  const units = Array.from(new Set(rows.map((it) => it.unit_code).filter(Boolean)));
  const unitSuffix = units.length === 1 ? units[0] : units.length > 1 ? "mixed UOM" : "";

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]" data-testid="wip-stage-page">
      <Toaster position="top-right" richColors />

      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">WIP by Stage</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-auto max-w-[1400px] w-full mx-auto px-6 py-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-xl font-bold text-[#1D2939]">In-Process Qty by Stage</h1>
            {updatedAt && (
              <span className="text-xs text-[#475467] font-sans bg-white border border-[#D0D5DD] rounded-full px-2.5 py-1" data-testid="wip-stage-last-updated">
                Last Updated: {formatIST(updatedAt)}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-[#0E7C86] font-sans bg-[#F0F9FA] border border-[#B7E4E8] rounded-full px-2.5 py-1" data-testid="wip-stage-auto-refresh-badge" title="Pulled from SAP at 9:15 am and 1:30 pm, then every 4 hours from 5:30 pm (5:30 pm, 9:30 pm, 1:30 am, 5:30 am) IST - or any time you click Refresh now">
              <Timer size={12} weight="bold" /> SAP pull 9:15 am, 1:30 pm, then every 4 hrs from 5:30 pm — next {nextRefreshLabel()} IST
            </span>
          </div>
          <Button
            type="button" variant="outline" onClick={refresh} disabled={refreshing}
            className="h-8 text-xs rounded-sm border-[#D0D5DD] text-[#344054]" data-testid="wip-stage-refresh-button"
            title={siteFilter !== "all" ? `Fast refresh - only site ${siteFilter}` : "Refresh all sites from SAP"}
          >
            <ArrowClockwise size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh now"}
          </Button>
        </div>

        <p className="text-xs text-[#667085] bg-[#F0F9FA] border border-[#B7E4E8] rounded-sm px-3 py-2" data-testid="wip-stage-disclaimer">
          Each stage shows what has been <strong>confirmed</strong> at it and what is still <strong>waiting</strong> for it (the quantity that cleared the previous stage but not this one; for the first stage, what is planned but not yet made). Estimated from Production Confirmation checkpoints - this is <strong>not</strong> a SAP-tracked stock location, unlike the main Inventory page.
        </p>

        {status === "loading" && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm p-8 text-center text-sm text-[#475467]" data-testid="wip-stage-loading">
            <ArrowClockwise size={20} className="animate-spin inline-block mb-2" />
            <p>Loading...</p>
          </div>
        )}

        {status === "failed" && (
          <div className="bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-4 text-sm text-[#912018]" data-testid="wip-stage-error">
            Could not load: {error}
          </div>
        )}

        {status === "done" && items.length === 0 && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm p-8 text-center text-sm text-[#475467]" data-testid="wip-stage-empty">
            Nothing to show right now - either every open lot is fully caught up through its last operation, or the background cache hasn't completed its first cycle yet.
          </div>
        )}

        {status === "done" && items.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={Stack} label="Stages Shown" value={rows.length.toLocaleString("en-IN")} testId="wip-stage-stat-buckets" />
              <StatCard icon={Package} label="Products Affected" value={distinctProducts.toLocaleString("en-IN")} testId="wip-stage-stat-products" />
              <StatCard icon={Factory} label="Lots Contributing" value={totalLots.toLocaleString("en-IN")} testId="wip-stage-stat-lots" />
              <StatCard icon={Timer} label="Total In-Process" value={formatQty(totalInProcess)} suffix={unitSuffix} testId="wip-stage-stat-total-in-process" />
            </div>

            <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                <input
                  type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search Product ID..." data-testid="wip-stage-search-input"
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#D0D5DD] rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0E7C86]"
                />
              </div>
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="w-32 h-8 text-xs rounded-sm border-[#D0D5DD]" data-testid="wip-stage-site-filter">
                  <SelectValue placeholder="Site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Oct 17 2026, user's ask: see the report for a date range. SAP
                  puts no date on an individual confirmation, and the Production
                  Lot query carries one on only a quarter of open lots - so the
                  range is matched against each order's SAP release date. */}
              <div className="flex items-center gap-1.5 flex-wrap" title="Filters on each order's SAP release date">
                <CalendarBlank size={14} className="text-[#667085]" />
                <input
                  type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate || undefined}
                  aria-label="Order released from" data-testid="wip-stage-from-date"
                  className="h-8 px-2 text-xs border border-[#D0D5DD] rounded-sm text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#0E7C86]"
                />
                <span className="text-xs text-[#667085]">to</span>
                <input
                  type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate || undefined}
                  aria-label="Order released to" data-testid="wip-stage-to-date"
                  className="h-8 px-2 text-xs border border-[#D0D5DD] rounded-sm text-[#344054] focus:outline-none focus:ring-1 focus:ring-[#0E7C86]"
                />
                {dateActive && (
                  <Button
                    type="button" variant="ghost" onClick={() => { setFromDate(""); setToDate(""); }}
                    className="h-8 px-2 text-xs text-[#0E7C86] hover:bg-[#F0F9FA]" data-testid="wip-stage-clear-dates"
                  >
                    Clear dates
                  </Button>
                )}
              </div>

              <span className="text-xs text-[#475467] ml-auto font-sans" data-testid="wip-stage-item-count">
                {rows.length} of {items.length} stage(s)
              </span>
            </div>

            {dateActive && (
              <p className="text-xs text-[#475467] bg-white border border-[#D0D5DD] rounded-sm px-3 py-2" data-testid="wip-stage-date-note">
                Showing lots whose order was <strong>released in SAP</strong>
                {fromDate ? ` on/after ${formatDay(fromDate)}` : ""}
                {fromDate && toDate ? " and" : ""}
                {toDate ? ` on/before ${formatDay(toDate)}` : ""}. Totals below are re-summed for this range.
                {undatedHidden > 0 && ` ${undatedHidden} lot contribution(s) have no release date in SAP and are excluded.`}
              </p>
            )}

            <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-auto max-h-[65vh]">
              <table className="w-full text-[13px] border-collapse block md:table">
                <thead className="hidden md:table-header-group md:sticky md:top-0 md:z-[1]">
                  <tr className="md:table-row">
                    <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 w-8"></th>
                    <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">Product ID</th>
                    <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">Site</th>
                    <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">Stage</th>
                    <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-right text-xs font-bold text-[#344054] font-heading uppercase">Confirmed Qty</th>
                    <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-right text-xs font-bold text-[#344054] font-heading uppercase">In-Process Qty</th>
                    <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">UOM</th>
                    <th className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-right text-xs font-bold text-[#344054] font-heading uppercase"># Lots</th>
                  </tr>
                </thead>
                <tbody className="block md:table-row-group">
                  {rows.map((it, i) => {
                    const rowKey = `${it.product_id}::${it.site_id}::${it.reporting_point_id}`;
                    const isExpanded = expandedRows.has(rowKey);
                    return (
                      <Fragment key={rowKey}>
                        <tr
                          className={`flex flex-col md:table-row mb-3 md:mb-0 last:mb-0 rounded-lg md:rounded-none border md:border-0 border-[#D0D5DD] overflow-hidden shadow-sm md:shadow-none ${i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} hover:bg-[#F0F4F8] cursor-pointer transition-colors duration-150`}
                          onClick={() => toggleRow(rowKey)}
                          data-testid={`wip-stage-row-${i}`}
                        >
                          <td className="hidden md:table-cell border border-[#D0D5DD] px-1.5 py-1 text-center text-[#667085]">
                            {isExpanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                          </td>
                          <td data-label="Product ID" className="flex md:table-cell justify-between items-center gap-3 border-b md:border border-[#D0D5DD] px-3 py-2 md:px-2 md:py-1 font-medium text-[#101828] before:content-[attr(data-label)] before:font-bold before:text-[10px] before:uppercase before:text-[#667085] before:shrink-0 md:before:content-none">
                            {it.product_id}
                          </td>
                          <td data-label="Site" className="flex md:table-cell justify-between items-center gap-3 border-b md:border border-[#D0D5DD] px-3 py-2 md:px-2 md:py-1 text-[#475467] before:content-[attr(data-label)] before:font-bold before:text-[10px] before:uppercase before:text-[#667085] before:shrink-0 md:before:content-none">
                            {it.site_id || "—"}
                          </td>
                          <td data-label="Stage" className="flex md:table-cell justify-between items-center gap-3 border-b md:border border-[#D0D5DD] px-3 py-2 md:px-2 md:py-1 text-[#101828] before:content-[attr(data-label)] before:font-bold before:text-[10px] before:uppercase before:text-[#667085] before:shrink-0 md:before:content-none">
                            {/* Oct 3 2026, user's ask: the stage NAME with its
                                Reporting Point id - "POWDER COATING (END)".
                                The backend fills operation_description from the
                                Bill of Operations (attach_stage_descriptions). */}
                            {it.operation_description ? (
                              <span data-testid={`wip-stage-label-${i}`}>
                                {it.operation_description}
                                <span className="text-[#98A2B3] text-[11px] ml-1">({it.reporting_point_id})</span>
                              </span>
                            ) : (
                              <span data-testid={`wip-stage-label-${i}`}>{it.reporting_point_id}</span>
                            )}
                          </td>
                          <td data-label="Confirmed Qty" className="flex md:table-cell justify-between items-center gap-3 border-b md:border border-[#D0D5DD] px-3 py-2 md:px-2 md:py-1 text-right font-semibold text-[#027A48] tabular-nums before:content-[attr(data-label)] before:font-bold before:text-[10px] before:uppercase before:text-[#667085] before:shrink-0 md:before:content-none" data-testid={`wip-stage-confirmed-${i}`}>
                            {formatQty(it.confirmed_qty)}
                          </td>
                          <td data-label="In-Process Qty" className="flex md:table-cell justify-between items-center gap-3 border-b md:border border-[#D0D5DD] px-3 py-2 md:px-2 md:py-1 text-right font-semibold tabular-nums before:content-[attr(data-label)] before:font-bold before:text-[10px] before:uppercase before:text-[#667085] before:shrink-0 md:before:content-none" data-testid={`wip-stage-inprocess-${i}`}>
                            <span className={it.in_process_qty > 0 ? "text-[#0E7C86]" : "text-[#98A2B3]"}>
                              {formatQty(it.in_process_qty)}
                            </span>
                          </td>
                          <td data-label="UOM" className="flex md:table-cell justify-between items-center gap-3 border-b md:border border-[#D0D5DD] px-3 py-2 md:px-2 md:py-1 text-[#475467] before:content-[attr(data-label)] before:font-bold before:text-[10px] before:uppercase before:text-[#667085] before:shrink-0 md:before:content-none">
                            {it.unit_code || "—"}
                          </td>
                          <td data-label="# Lots" className="flex md:table-cell justify-between items-center gap-3 border-b md:border border-[#D0D5DD] px-3 py-2 md:px-2 md:py-1 text-right text-[#475467] tabular-nums before:content-[attr(data-label)] before:font-bold before:text-[10px] before:uppercase before:text-[#667085] before:shrink-0 md:before:content-none">
                            {it.lots.length}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="md:table-row" data-testid={`wip-stage-expand-${i}`}>
                            <td colSpan={8} className="border border-[#D0D5DD] bg-[#F9FAFB] px-3 py-2">
                              <div className="text-[11px] font-bold uppercase tracking-wide text-[#667085] mb-1.5">Contributing Lots</div>
                              <div className="flex flex-wrap gap-1.5">
                                {it.lots.map((l) => (
                                  <span key={l.production_lot_id} className="inline-flex items-center gap-1.5 bg-white border border-[#D0D5DD] rounded-full px-2.5 py-1 text-[12px]" data-testid={`wip-stage-lot-${l.production_lot_id}`}>
                                    <span className="font-medium text-[#101828]">{l.production_lot_id}</span>
                                    <span className="text-[#027A48]">confirmed {formatQty(l.confirmed_qty)}</span>
                                    <span className="text-[#667085]">· in-process {formatQty(l.qty)} {it.unit_code}</span>
                                    <span className="text-[#98A2B3]">· released {formatDay(lotDate(l))}</span>
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="border border-[#D0D5DD] text-center py-6 text-[13px] text-[#475467]">No matching stages{dateActive ? " for this date range" : ""}.</td></tr>
                  )}
                </tbody>
                {rows.length > 0 && (
                  <tfoot className="block md:table-footer-group md:sticky md:bottom-0">
                    <tr className="md:table-row" data-testid="wip-stage-totals-row">
                      <td className="hidden md:table-cell bg-[#EAECF0] border border-[#D0D5DD]"></td>
                      <td colSpan={3} className="bg-[#EAECF0] border border-[#D0D5DD] px-2 py-1.5 text-xs font-bold uppercase text-[#344054] font-heading">
                        Total ({rows.length} stage{rows.length === 1 ? "" : "s"}{dateActive ? ", filtered by date" : ""})
                      </td>
                      <td className="bg-[#EAECF0] border border-[#D0D5DD] px-2 py-1.5 text-right font-bold tabular-nums text-[#027A48]" data-testid="wip-stage-total-confirmed">
                        {formatQty(totalConfirmed)}
                      </td>
                      <td className="bg-[#EAECF0] border border-[#D0D5DD] px-2 py-1.5 text-right font-bold tabular-nums text-[#0E7C86]" data-testid="wip-stage-total-inprocess">
                        {formatQty(totalInProcess)}
                      </td>
                      <td className="bg-[#EAECF0] border border-[#D0D5DD] px-2 py-1.5 text-xs text-[#475467]">{unitSuffix}</td>
                      <td className="bg-[#EAECF0] border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums text-[#475467]">{totalLots}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function round4(value) {
  return Math.round((value + Number.EPSILON) * 10000) / 10000;
}
